import { useAtomValue, useSetAtom } from 'jotai'
import { useMemo } from 'react'
import type { Poi, Tag } from '@/js/types'
import {
    activeTagsAtom,
    filteredPoisAtom,
    hasFilterResultsAtom,
    searchQueryAtom,
    setActiveTagIdsAtom,
    setSearchQueryAtom,
    toggleTagFilterAtom,
} from '@/js/editor/state/filterAtoms.ts'

export const useSearch = (sourcePois?: Array<Poi>) => {
    const query = useAtomValue(searchQueryAtom)
    const activeTags = useAtomValue(activeTagsAtom)
    const canonicalResults = useAtomValue(filteredPoisAtom)
    const canonicalHasResults = useAtomValue(hasFilterResultsAtom)

    const setQuery = useSetAtom(setSearchQueryAtom)
    const setActiveTagIds = useSetAtom(setActiveTagIdsAtom)
    const toggleTagFilter = useSetAtom(toggleTagFilterAtom)

    const results = useMemo(() => {
        if (!sourcePois) {
            return canonicalResults
        }

        const normalizedQuery = query.trim().toLowerCase()
        const activeTagIds = activeTags.map((tag) => tag.id)

        return sourcePois.filter((poi) => {
            if (normalizedQuery && !poi.title.toLowerCase().includes(normalizedQuery)) {
                return false
            }

            return activeTagIds.every((tagId) => poi.tags.some((tag) => String(tag.id) === String(tagId)))
        })
    }, [activeTags, canonicalResults, query, sourcePois])

    const hasResults = sourcePois ? results.length > 0 : canonicalHasResults

    const filterByTag = (tag: Tag) => {
        if (!tag) {
            return
        }

        toggleTagFilter(tag.id)
    }

    return {
        results,
        hasResults,
        query,
        activeTags,
        search: setQuery,
        filter: filterByTag,
        filterTag: setActiveTagIds,
    }
}
